import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { CATEGORIES } from '@/data';
import type { ScheduledActivity, Clip } from '@/types';

interface DraggableActivityProps {
  activity: ScheduledActivity;
  clip: Clip | undefined;
  onClick?: () => void;
}

export const DraggableActivity: React.FC<DraggableActivityProps> = ({
  activity,
  clip,
  onClick,
}) => {
  const category = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `activity-${activity.id}`,
    data: {
      type: 'activity',
      activity,
      clip,
    },
  });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  return (
    <button
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        // Only trigger click if not dragging
        if (!isDragging && onClick) {
          e.stopPropagation();
          onClick();
        }
      }}
      className={`
        w-full text-left px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs
        truncate flex items-center gap-1
        ${activity.completedAt
          ? 'bg-green-500/20 text-green-400'
          : 'bg-orange-500/20 text-orange-400'
        }
        hover:opacity-80 transition-opacity
        ${isDragging ? 'cursor-grabbing z-50' : 'cursor-grab'}
        touch-none
      `}
    >
      <span className="flex-shrink-0">{category?.emoji || '📎'}</span>
      <span className="truncate hidden md:inline">{clip?.title || 'Unknown'}</span>
    </button>
  );
};
